import React, {useCallback, useMemo, useRef, useState} from 'react'
import {connect} from 'react-redux'
import {DndContext, DragOverlay, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors} from '@dnd-kit/core'
import {SortableContext, sortableKeyboardCoordinates, horizontalListSortingStrategy, arrayMove} from '@dnd-kit/sortable'
import {restrictToHorizontalAxis} from '@dnd-kit/modifiers'

import {
    addOrders,
    changeOrderState,
    changeStatus,
    getOrder,
    resetOrder
} from '../../Redux/actions/orderActions'
import {changeVisibleState} from '../../Redux/actions'
import {changeBookState} from '../../Redux/actions/bookActions'

import Equipment from '../general/cell/Equipment'
import StikerToPrint from './newOrder/orderHisroy/StikerToPrint'
import OrdersHeaderCell from './OrdersHeaderCell'
import Label from '../general/cell/Label'
import Loader from '../Loader/Loader'
import CreateAt from '../general/cell/CreateAt'
import EstimatedDoneAt from '../general/cell/EstimatedDoneAt'
import Status from '../general/cell/Status'
import Data from '../general/cell/Data'
import Client from '../general/cell/Client'
import {changePaymentState} from "../../Redux/actions/paymentAction";


const estimatedDoneStatusGroups = [1, 2, 3]

const OrderRow = React.memo(function OrderRow({order, headers, listOfGroups, tableOrderRef, onEdit, onGetOrder, onSetStatus}) {

    const chooseCell = (field) => {
        switch (field.id) {

            case 1:
                return (
                <Label
                    key={field.id}
                    label={order.id_label}
                    func={() => onGetOrder(order.id)}
                    urgent={order.status.group < 3 && order.urgent}
                />
            )
            case 2:
                return (
                <CreateAt
                    key={field.id}
                    creator={order.created_by.name}
                    date={order.created_at}/>
            )
            case 3:
                return (
                <EstimatedDoneAt
                    key={field.id}
                    estimatedDoneAt={order.estimated_done_at}
                    statusGroupId={order.status.group}
                    listStatusGroup={estimatedDoneStatusGroups}
                />
            )
            case 4: return (
                <Status
                    key={field.id}
                    id={order.id * order.status.id}
                    status={order.status}
                    listOfGroups={listOfGroups}
                    changeStatus={status => onSetStatus(status, order)}
                    tableOrderRef={tableOrderRef}
                />
            )
            case 5: return <Equipment key={field.id} order={order}/>
            case 6: return <Data key={field.id} data={order.kindof_good.title}/>
            case 7: return <Data key={field.id} data={order.brand.title}/>
            case 8: return <Data key={field.id} data={order.subtype.title}/>
            case 10: return <Data key={field.id} data={order.engineer.name}/>
            case 11: return <Data key={field.id} data={order.manager.name}/>
            case 12: return <Client key={field.id} client={order.client}/>
            case 18: return <Data key={field.id} data={order.ad_campaign.name}/>
            default: return <Data key={field.id} data={order[field.field]}/>
        }
    }

    return (
        <tr className="tr" onDoubleClick={() => onEdit(order)}>
            {headers.map(field => chooseCell(field))}
        </tr>
    )
})


const TableOrders = props => {

    const tableOrderRef = useRef(null)
    const tableElementRef = useRef(null)
    const columnDragHappened = useRef(false)
    const [activeColumnId, setActiveColumnId] = useState(null)

    const {getOrder, changeBookState, changePaymentState, changeVisibleState, changeStatus, changeOrderState, resetOrder} = props
    const itemPayments = props.item_payments
    const currentUser = props.current_user
    const needToResetOrder = props.view.needToResetOrder
    const tableHeadersAll = props.order.table_headers
    const sortDirection = props.order.sort

    const sensors = useSensors(
        useSensor(PointerSensor, {activationConstraint: {distance: 5}}),
        useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates})
    )

    const handleEdit = useCallback((order) => {
        changeBookState({
            equipment_type: order.kindof_good,
            equipment_brand: order.brand,
            equipment_subtype: order.subtype,
            equipment_model: order.model
        })
        getOrder(order.id)
    }, [changeBookState, getOrder])

    const onGetOrder = useCallback((id) => getOrder(id), [getOrder])

    const afterPrint = useCallback(() => {
        changeVisibleState({statusOrderSticker: false})
        if (needToResetOrder) {
            resetOrder()
            changeVisibleState({needToResetOrder: false})
        }
    }, [changeVisibleState, resetOrder, needToResetOrder])

    const handleSetStatus = useCallback((status, order) => {

        const paymentRequiredGroup = 6

        if (status.group === paymentRequiredGroup && order.price !== order.payed) {

            const isIncome = order.price > order.payed
            const cahsflowCategory = itemPayments.find(item => item.id === (isIncome ? 2 : 8))

            changePaymentState({
                direction: isIncome ? 2 : 1,
                [isIncome ? 'income': 'outcome']: Math.abs(order.missed_payments),
                client: order.client,
                description: isIncome ? `Оплата по заказу № ${order.id_label}` : `Выплата по заказу № ${order.id_label}`,
                cashflow_category: cahsflowCategory,
                employee: currentUser,
                order_id: order.id,
                context: {
                    type: 'closed_order',
                    order_id: order.id,
                    status_id: status.id
                }
            })
            changeVisibleState({'statusPaymentsEditor': true})
        } else {
            changeStatus(status.id, order.id)
        }

    }, [itemPayments, currentUser, changePaymentState, changeVisibleState, changeStatus])

    const handleHeaderSort = useCallback((header) => {
        if (columnDragHappened.current) {
            columnDragHappened.current = false
            return
        }
        changeOrderState({field_sort: header.field, sort: sortDirection === 'asc' ? 'desc' : 'asc'})
    }, [changeOrderState, sortDirection])

    const handleColumnResize = useCallback((headerId, width) => {
        const newHeaders = tableHeadersAll.map(header => header.id === headerId ? {...header, width} : header)
        changeOrderState({table_headers: newHeaders})
    }, [changeOrderState, tableHeadersAll])

    const listOfGroups = useMemo(() => props.status_group.filter(group => group.id < 8),
        [props.status_group])

    const headers = useMemo(
        () => tableHeadersAll.filter(header => header.visible).sort((a, b) => a.order - b.order),
        [tableHeadersAll]
    )

    const headerIds = useMemo(() => headers.map(header => header.id), [headers])

    const columnSizeVars = useMemo(() => {
        const vars = {}
        let total = 0
        headers.forEach(header => {
            vars[`--col-${header.id}-size`] = header.width
            total += header.width
        })
        vars['--orders-table-width'] = total
        return vars
    }, [headers])

    const handleColumnResizeMove = useCallback((headerId, width) => {
        const table = tableElementRef.current
        if (!table) return
        table.style.setProperty(`--col-${headerId}-size`, width)
        let total = 0
        headers.forEach(header => { total += header.id === headerId ? width : header.width })
        table.style.setProperty('--orders-table-width', total)
    }, [headers])

    const handleColumnDragStart = (event) => {
        columnDragHappened.current = true
        setActiveColumnId(event.active.id)
    }

    const handleColumnDragCancel = () => setActiveColumnId(null)

    const handleColumnDragEnd = (event) => {
        setActiveColumnId(null)
        const {active, over} = event
        if (!over || active.id === over.id) return

        const oldIndex = headers.findIndex(header => header.id === active.id)
        const newIndex = headers.findIndex(header => header.id === over.id)
        if (oldIndex === -1 || newIndex === -1) return

        const reordered = arrayMove(headers, oldIndex, newIndex)
        const orderById = {}
        reordered.forEach((header, index) => { orderById[header.id] = index })

        let cursor = reordered.length
        tableHeadersAll
            .filter(header => !header.visible)
            .sort((a, b) => a.order - b.order)
            .forEach(header => { orderById[header.id] = cursor++ })

        const newHeaders = tableHeadersAll.map(header => ({...header, order: orderById[header.id] ?? header.order}))
        changeOrderState({table_headers: newHeaders})
    }

    if (!props.employees) return <Loader/>

    const isColdLoad = props.order.spinner && props.order.ordersShow.length === 0
    if (isColdLoad) return <Loader/>

    const activeHeader = activeColumnId != null ? headers.find(header => header.id === activeColumnId) : null

    return (
        <div className="table-orders-wrapper">
            {props.order.spinner ? (
                <div className="table-loading-overlay" aria-busy="true">
                    <Loader/>
                </div>
            ) : null}
            <div className="table-orders-container" ref={tableOrderRef}>
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    modifiers={[restrictToHorizontalAxis]}
                    onDragStart={handleColumnDragStart}
                    onDragEnd={handleColumnDragEnd}
                    onDragCancel={handleColumnDragCancel}
                >
                    <table className="orders-table" ref={tableElementRef} style={columnSizeVars}>
                        <colgroup>
                            {headers.map(header => (
                                <col key={header.id} style={{width: `calc(var(--col-${header.id}-size) * 1px)`}}/>
                            ))}
                        </colgroup>
                        <thead className="tableThead">
                        <tr>
                            <SortableContext items={headerIds} strategy={horizontalListSortingStrategy}>
                                {headers.map(header => (
                                    <OrdersHeaderCell
                                        key={header.id}
                                        header={header}
                                        sortField={props.order.field_sort}
                                        sortDirection={sortDirection}
                                        onSortClick={handleHeaderSort}
                                        onResizeMove={handleColumnResizeMove}
                                        onResizeCommit={handleColumnResize}
                                    />
                                ))}
                            </SortableContext>
                        </tr>
                        </thead>
                        <tbody>
                        {props.order.ordersShow.map(order => (
                            <OrderRow
                                key={order.id}
                                order={order}
                                headers={headers}
                                listOfGroups={listOfGroups}
                                tableOrderRef={tableOrderRef}
                                onEdit={handleEdit}
                                onGetOrder={onGetOrder}
                                onSetStatus={handleSetStatus}
                            />
                        ))}
                        </tbody>
                    </table>
                    <DragOverlay>
                        {activeHeader ? (
                            <div className="table-header-drag-overlay">{activeHeader.title}</div>
                        ) : null}
                    </DragOverlay>
                </DndContext>
                {props.view.statusOrderSticker ? <StikerToPrint onAfterPrint={afterPrint}/> : null}
                <div className='h100p w250'/>
            </div>
        </div>
    )
}

const mapStateToProps = state => ({
    order: state.order,
    employees: state.employee.employees,
    user: state.data.user,
    view: state.view,
    mainFilter: state.filter.mainFilter,
    status_group: state.data.status_group,
    current_user: state.data.user,
    item_payments: state.data.item_payments
})

const mapDispatchToProps = {
    addOrders,
    changeVisibleState,
    changePaymentState,
    changeBookState,
    resetOrder,
    getOrder,
    changeOrderState,
    changeStatus
}

export default connect(mapStateToProps, mapDispatchToProps)(TableOrders)
